import {
  EmptyInput,
  InputGroup,
  InputGroupBreak,
  InputRow,
  ModelInput,
  ModelNewNumericInput,
  ModelSelect,
  ModelSelectSearchInput,
  RadioYesNoInput,
} from '@equifinance/shared';
import { StaticDataItem } from '../../../../../api/useAccountApi';
import {
  EmploymentType,
  NotWorkingStatus,
  useAccountContext,
} from '../../../../../contexts';
import { EmploymentHistory } from '../../../../../contexts/account/IncomeAndEmployment';

function IndustrySelect({ industries, field }: { industries: StaticDataItem[], field: string }) {
  return (
    <ModelSelectSearchInput
      label="Industry"
      placeholder="Please select"
      field={`${field}.industryCode`}
      options={
        industries.reduce((prev, { key, label }) => ({
          ...prev,
          [key]: label,
        }), {})
      }
      required
    />
  );
}

export default function PositionSectionEmploymentType({
  employmentHistory,
  industries,
  jobTitles,
  field,
}: {
  employmentHistory: EmploymentHistory,
  industries: StaticDataItem[],
  jobTitles: StaticDataItem[],
  field: string,
}) {
  const employedOrCIS = [
    'Employed',
    'ConstructionIndustryScheme',
  ].includes(employmentHistory?.employmentType || '');

  const { updateAccount } = useAccountContext();

  const update = (values: Record<string, any>, disableIncrementUpdateCount: boolean = false) => {
    Object.entries(values).forEach(([, value]) => {
      updateAccount(
        field,
        {
          id: employmentHistory.id,
          employmentType: value,
        },
        disableIncrementUpdateCount,
      );
    });
  };

  return (
    <>
      <InputGroup>
        <InputRow>
          <ModelSelect
            label="Employment status"
            placeholder="Please select"
            field={`${field}.employmentType`}
            updateOverride={update}
            options={EmploymentType}
            required
          />
          {employmentHistory?.employmentType === 'NotWorkingNotRetired' ? (
            <ModelSelect
              label="Status"
              field={`${field}.status`}
              options={NotWorkingStatus}
              required
            />
          ) : <EmptyInput />}
        </InputRow>
        {employedOrCIS && (
          <InputRow>
            <ModelSelectSearchInput
              label="Job title"
              placeholder="Please select"
              field={`${field}.jobTitleCode`}
              options={
                jobTitles.reduce((prev, { key, label }) => ({
                  ...prev,
                  [key]: label,
                }), {})
              }
              required
            />
            <IndustrySelect field={field} industries={industries} />
          </InputRow>
        )}
        {(employmentHistory?.employmentType === 'SoleTrader') && (
          <InputRow>
            <ModelInput
              label="Trading name"
              field={`${field}.tradingName`}
              required
            />
            <IndustrySelect field={field} industries={industries} />
          </InputRow>
        )}
        {(employmentHistory?.employmentType === 'Partnership') && (
          <InputRow>
            <ModelInput
              label="Company name"
              field={`${field}.companyName`}
              required
            />
            <IndustrySelect field={field} industries={industries} />
          </InputRow>
        )}
      </InputGroup>
      {(employmentHistory?.employmentType === 'DirectorOfLimitedCompany') && (
        <>
          <InputGroup>
            <InputRow>
              <RadioYesNoInput
                label="Applicant is a shareholder"
                field={`${field}.isShareholder`}
                required
              />
              {employmentHistory?.isShareholder && (
                <ModelNewNumericInput
                  label="Shareholding"
                  field={`${field}.shareholdingPercentage`}
                  suffix="%"
                  isFixedDecimal
                  required
                />
              )}
            </InputRow>
          </InputGroup>
          <InputGroupBreak />
        </>
      )}
      {[
        'LimitedLiabilityPartnership',
        'DirectorOfLimitedCompany',
      ].includes(employmentHistory?.employmentType || '') && (
      <InputGroup>
        <InputRow>
          <ModelInput
            label="Company name"
            field={`${field}.companyName`}
            required
          />
          <ModelInput
            label="Company registration number"
            field={`${field}.companyRegistrationNumber`}
          />
        </InputRow>
        <InputRow>
          {employmentHistory?.employmentType === 'LimitedLiabilityPartnership' && (
          <ModelNewNumericInput
            label="Share of ownership"
            field={`${field}.shareOfOwnership`}
            suffix="%"
            isFixedDecimal
            required
          />
          )}
          <IndustrySelect field={field} industries={industries} />
          {employmentHistory?.employmentType !== 'LimitedLiabilityPartnership' && <EmptyInput />}
        </InputRow>
      </InputGroup>
      )}
      {(employmentHistory?.employmentType === 'FosterCarer') && (
        <>
          <InputGroupBreak />
          <InputGroup>
            <InputRow>
              <ModelInput
                label="Agency name"
                field={`${field}.agencyName`}
                required
              />
              <EmptyInput />
            </InputRow>
          </InputGroup>
        </>
      )}
    </>
  );
}
